import styled from '@emotion/styled';
import { ViewType } from 'src/AppState/WithdrawalState';
import { affordabilityExcludedColors, fraudAndComplianceColors } from 'src/assets/colors';
import { ChevronDownIcon } from 'src/assets/icons/ChevronDownIcon';
import { Button } from 'src/domains/layouts/shared/buttons/Button';

export const TableWrapperTemplate = styled('table', { label: 'TableWrapperTemplate' })`
    margin-bottom: 16px;
`;

export const TableHeaderRowTemplate = styled('tr', { label: 'TableHeaderRowTemplate' })`
    position: relative;
    border: none;
`;

export interface TablePropsType {
    section: ViewType | 'Red' | 'Amber' | 'Green'
}

interface TableCellType {
    disableColor?: boolean;
}

export const buildRowBgColor = (section: ViewType | 'Red' | 'Amber' | 'Green'): string => {
    //remove !important after delete '.fraud-and-compliances-tab'
    if (section === 'your') {
        return `
            background-color: ${fraudAndComplianceColors.commonTable.bgColorRowYourSecond} !important;
            :nth-of-type(odd) {
                background-color: ${fraudAndComplianceColors.commonTable.bgColorRowYourFirst} !important;
            }
            `;
    }
    if (section === 'none') {
        return `
            background-color: ${fraudAndComplianceColors.commonTable.bgColorRowPendingSecond} !important;
            :nth-of-type(odd) {
                background-color: ${fraudAndComplianceColors.commonTable.bgColorRowPendingFirst} !important;
            }
        `;
    }
    if (section === 'Red' || section === 'Amber' || section === 'Green') {
        return `
            background-color: ${affordabilityExcludedColors.evenTableColor} !important;
            :nth-of-type(odd) {
                background-color: ${affordabilityExcludedColors.oddTableColor} !important;
            }
        `;
    }
    return `
        background-color: ${fraudAndComplianceColors.commonTable.bgColorRowOtherSecond} !important;
        :nth-of-type(odd) {
            background-color: ${fraudAndComplianceColors.commonTable.bgColorRowOtherFirst} !important;
        }
    `;
};

export const buildBorderRightColor = (section: ViewType | 'Red' | 'Amber' | 'Green'): string => {
    if (section === 'your') {
        return `
            ${fraudAndComplianceColors.commonTable.borderColorYour};
        `;
    }
    if (section === 'none') {
        return `
            ${fraudAndComplianceColors.commonTable.borderColorPending};
        `;
    }
    return `
        ${fraudAndComplianceColors.commonTable.borderColorOther};
    `;
};


export const buildCustomerBtnBgColor = (section: ViewType | 'Red' | 'Amber' | 'Green'): string => {
    if (section === 'other') {
        return `
            ${fraudAndComplianceColors.commonTable.bgColorRowOtherFirst};
        `;
    }
    return `
        ${fraudAndComplianceColors.commonTable.bgColor};
    `;
};

/* border to be removed later */
export const TableRowTemplate = styled('tr', { label: 'TableRowTemplate' }) <TablePropsType>`
    border: none;
    ${(props): string => buildRowBgColor(props.section)};
    td {
        :nth-last-of-type(1), :nth-last-of-type(2) {
            ::after {
                content: none;
            }
        }
    }
    :nth-of-type(even) {
        td > div {
            background-color: ${(props): string => buildCustomerBtnBgColor(props.section)};
        }
    }
    :nth-of-type(even) {
        td {
            ::after {
                border-color: ${(props): string => buildBorderRightColor(props.section)};
            }
        }
    }
`;

export const ColumnHeaderTemplate = styled('th', { label: 'ColumnHeaderTemplate' })`
    position: relative;
    font-size: 10px;
    font-weight: bold;
    line-height: 1.2;
    color: ${fraudAndComplianceColors.commonTable.txtColor};
    padding: 10px 7px 10px 8px !important;
    ::after {
        content: '';
        border-right: 1px solid ${fraudAndComplianceColors.commonTable.borderColorDefault};
        position: absolute;
        right: 0;
        top: 4px;
        bottom: 4px;
        margin: auto 0;
    }
`;

export const TableCellTemplate = styled('td', { label: 'TableCellTemplate' })<TableCellType>`
    position: relative;
    text-align: left;
    font-size: 12px;
    line-height: 1;
    color: ${fraudAndComplianceColors.commonTable.txtColor};
    padding: 4px 8px;
    ${({disableColor}): string => disableColor === true ? `background-color: ${fraudAndComplianceColors.commonTable.bgColor};` : '' }
    ::after {
        border-right: 1px solid ${fraudAndComplianceColors.commonTable.borderColorWhite};
        bottom: 4px;
        content: '';
        margin: auto 0;
        position: absolute;
        right: 0;
        top: 4px;
    }
`;

export const TableSelectCellTemplate = styled('td', { label: 'TableSelectCellTemplate' })`
    background-color: ${fraudAndComplianceColors.commonTable.bgColor};
    padding: 4px 0 !important;
`;

export const SectionMessageTemplate = styled('div', { label: 'SectionMessageTemplate' })`
    font-size: 14px;
    padding: 6px;
    margin-bottom: 16px;
    background-color: ${fraudAndComplianceColors.commonTable.bgColorMessage};
    color: ${fraudAndComplianceColors.commonTable.txtColorMessage};
    border-radius: 4px;
`;

export interface ChevronDownType {
    isCollapsed: boolean;
}

export const ChevronDownWrapperTemplate = styled(ChevronDownIcon, { label: 'ChevronDownWrapperTemplate' }) <ChevronDownType>`
    fill: ${fraudAndComplianceColors.commonTable.iconColorChevron};
    margin: 0 calc(50% - 6px);
    transform: ${({ isCollapsed }): string => isCollapsed === true ? 'rotate(0)' : 'rotate(180deg)'};
    transition: all 0.5s;
    width: 12px;
`;

export const TableSelectCellClickableTemplate = styled(TableCellTemplate, { label: 'TableSelectCellClickableTemplate' })`
    cursor: pointer;
`;

export const SelectWrapper = styled('div', { label: 'SelectWrapper' })`
    position: relative;
    margin: 0 8px;
`;

export const Select = styled('select', { label: 'Select' })`
    font-size: 14px;
    background-color: ${fraudAndComplianceColors.commonTable.bgColorSelect};
    color: ${fraudAndComplianceColors.commonTable.txtColor};
    appearance: none;
    width: 100%;
    padding: 7px 0 7px 8px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
`;

export const ChevronDownIconWrapper = styled(ChevronDownIcon, { label: 'ChevronDownIconWrapper' })`
    fill: currentColor;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 12px;
    margin: auto;
    pointer-events: none;
`;

export const CustomerButtonWrapper = styled('div', { label: 'CustomerButtonWrapper' })`
    background: ${fraudAndComplianceColors.withdrawalReqColors.bgColor};
    border-radius: 2px;
    cursor: pointer;
    padding: 4px 8px;
    position: relative;
    width: 100%;
`;

export const CustomerName = styled('span', { label: 'CustomerName' })`
    font-size: 10px;
    line-height: 1.2;
    color: ${fraudAndComplianceColors.withdrawalReqColors.txtColor};
`;

export const CustomerId = styled('span', { label: 'CustomerId' })`
    color: ${fraudAndComplianceColors.withdrawalReqColors.txtColorCustomerId};
    font-size: 10px;
    line-height: 1.1;
    display: block;
`;

export const ButtonWrapper = styled(Button, { label: 'ButtonWrapper' })`
    margin: auto;
`;
